// @layers/client — Web browser SDK.
// Thin wrapper over @layers/core-wasm. Owns only:
//   - localStorage persistence
//   - window online/offline detection
//   - navigator device info
//   - visibilitychange / beforeunload flush
//   - Install ID persistence (localStorage)
//   - Deep link handling/parsing (popstate, hashchange)
//   - setAttributionData API
//   - Install event gating (24h window)
//   - Debug overlay (DOM-based)
//   - Init timing measurement
//   - Full lifecycle events (app_open, app_background, app_foreground)
//   - Queue depth gating
import {
  FetchHttpClient,
  LayersCore,
  LayersError,
  createDefaultPersistence,
  initWasm,
  isWasmReady
} from '@layers/core-wasm';
import type {
  BeforeSendHook,
  ConsentState,
  DeviceContext,
  Environment,
  EventProperties,
  FeatureFlagBootstrap,
  FeatureFlagValue,
  FeatureFlagsListener,
  GroupsState,
  UserProperties
} from '@layers/core-wasm';

import { captureAttribution, getAttributionProperties } from './attribution.js';
import { getCapiProperties } from './capi.js';
import {
  disableDebugOverlay as hideDebugOverlay,
  enableDebugOverlay as showDebugOverlay
} from './debug-overlay.js';
import { parseDeepLink, setupDeepLinkListener } from './deep-links.js';
import type { DeepLinkData } from './deep-links.js';
import { installExceptionAutoCapture } from './exceptions.js';
import { installPageviewTracking } from './pageview-tracking.js';
import type { PageviewTrackingHandle } from './pageview-tracking.js';
import { type ReplayManager, installReplay, resolveReplayConfig } from './replay/index.js';
import type { ReplayConfig } from './replay/index.js';
import { SurveysModule } from './surveys/module.js';

export type {
  BeforeSendEvent,
  BeforeSendHook,
  ConsentState,
  DeviceContext,
  EcommerceItem,
  Environment,
  EventProperties,
  GroupsState,
  UserProperties,
  // Tier 4 — feature flags
  FeatureFlagValue,
  FeatureFlagBootstrap,
  FeatureFlagBootstrapData,
  FeatureFlagsListener,
  FeatureFlagDefinition,
  FeatureFlagCondition,
  FeatureFlagVariant
} from '@layers/core-wasm';

export { LayersError } from '@layers/core-wasm';

// Re-export the Tier 1 typed events catalog. Builders are renamed with a
// `build*` prefix to avoid collision with the legacy single-arg helpers
// already exported from `./standard-events.js` (purchaseEvent, addToCartEvent,
// etc.). The new builders accept structured params + an `items` array.
export { LayersEvent } from '@layers/core-wasm';
export type {
  LayersEventName,
  TypedEventPayload,
  PurchaseEventParams,
  AddToCartEventParams,
  BeginCheckoutEventParams,
  ViewItemEventParams,
  RefundEventParams,
  SubscribeEventParams,
  StartTrialEventParams
} from '@layers/core-wasm';
export {
  purchaseEvent as buildPurchaseEvent,
  addToCartEvent as buildAddToCartEvent,
  beginCheckoutEvent as buildBeginCheckoutEvent,
  viewItemEvent as buildViewItemEvent,
  refundEvent as buildRefundEvent,
  subscribeEvent as buildSubscribeEvent,
  startTrialEvent as buildStartTrialEvent,
  onboardingStartEvent as buildOnboardingStartEvent,
  onboardingCompleteEvent as buildOnboardingCompleteEvent,
  paywallShowEvent as buildPaywallShowEvent
} from '@layers/core-wasm';

export * from './api-types.js';
export type { AttributionData } from './attribution.js';
export { getAttribution, getAttributionProperties } from './attribution.js';
export {
  getCapiProperties,
  getFbpCookie,
  getOrCreateFbp,
  getTtpCookie,
  getPageUrl,
  getFbc,
  formatFbc
} from './capi.js';

export { StandardEvents } from './standard-events.js';
export type { StandardEventName, StandardEventPayload } from './standard-events.js';
export {
  loginEvent,
  signUpEvent,
  registerEvent,
  purchaseEvent,
  addToCartEvent,
  addToWishlistEvent,
  initiateCheckoutEvent,
  startTrialEvent,
  subscribeEvent,
  levelStartEvent,
  levelCompleteEvent,
  tutorialCompleteEvent,
  searchEvent,
  viewItemEvent,
  viewContentEvent,
  shareEvent,
  screenViewEvent,
  onboardingStartEvent,
  onboardingCompleteEvent,
  paywallShowEvent
} from './standard-events.js';

export type { DeepLinkData } from './deep-links.js';
export { parseDeepLink, setupDeepLinkListener } from './deep-links.js';
export type { DebugOverlayState } from './debug-overlay.js';

export type { ReplayConfig, SnapshotChunk, ResolvedReplayConfig } from './replay/index.js';
export { ReplayManager } from './replay/index.js';
// Tier 8 — Surveys & in-product messaging
export { SurveysModule, SurveyRenderer } from './surveys/index.js';
export type { SurveysModuleOptions, SurveyRendererCallbacks } from './surveys/index.js';
export type {
  SurveyAnswer,
  SurveyDefinition,
  SurveyDisplay,
  SurveyPosition,
  SurveyQuestion,
  SurveyResponse,
  SurveyTargeting,
  SurveyTargetingContext,
  SurveyType
} from '@layers/core-wasm';

export type {
  WebCommerceTracker,
  PurchaseParams,
  SubscriptionParams,
  CartItem,
  OrderParams,
  RefundParams,
  PurchaseFailedParams
} from './commerce.js';
export {
  trackPurchase,
  trackPurchaseFailed,
  trackSubscription,
  trackOrder,
  trackAddToCart,
  trackRemoveFromCart,
  trackBeginCheckout,
  trackViewProduct,
  trackRefund
} from './commerce.js';

export interface LayersConfig {
  /** Unique application identifier issued by the Layers dashboard. */
  appId: string;
  /** Deployment environment label. @default "production" */
  environment?: Environment;
  /** Optional user identifier to associate events with from the start. */
  appUserId?: string;
  /** Enable verbose debug logging to the console. @default false */
  enableDebug?: boolean;
  /**
   * Enable per-device DebugView. The SDK generates and persists a stable
   * `debug_token` (UUID) and sends `X-Debug-Token: <uuid>` on every event
   * upload. Use {@link LayersClient.getDebugToken} to retrieve the token
   * for displaying in dev UIs.
   * @default false
   */
  debug?: boolean;
  /** Base URL for the Layers ingest API. @default "https://in.layers.com" */
  baseUrl?: string;
  /**
   * Host for ATT-authorized iOS batches, accepted for config parity with mobile SDKs.
   * This SDK has no ATT and never sends to it. Defaults to https://att.layers.com.
   */
  trackingBaseUrl?: string;
  /**
   * Absolute URL of the SDK's WASM binary (`layers_core_bg.wasm`).
   *
   * Leave it unset with a bundler: the binary is emitted as an asset and
   * located automatically. Set it when you serve the binary from somewhere the
   * SDK cannot infer — a different CDN path, a versioned asset host, or a
   * `<script>` tag that cannot see its own `src`. If the binary cannot be
   * fetched the SDK keeps working on its portable JS engine and says so once
   * in the console.
   */
  wasmUrl?: string;
  /** How often the event queue is flushed, in milliseconds. @default 10000 */
  flushIntervalMs?: number;
  /** Number of queued events that triggers an automatic flush. @default 10 */
  flushThreshold?: number;
  /** Maximum number of events to hold in the queue before dropping. @default 1000 */
  maxQueueSize?: number;
  /**
   * Whether to automatically fire an `app_open` event during init().
   * Set to `false` if you want to fire the event manually.
   * @default true
   */
  autoTrackAppOpen?: boolean;
  /**
   * Whether to automatically track `deep_link_opened` events when the URL
   * changes (popstate/hashchange). The tracked event includes the parsed
   * URL components and all query parameters.
   * @default true
   */
  autoTrackDeepLinks?: boolean;
  /**
   * Whether to install global `error` and `unhandledrejection` listeners
   * that translate uncaught exceptions and unhandled promise rejections
   * into `$exception` events. Disable if you have your own crash reporter
   * already chained at window-level and don't want both fighting.
   * @default true
   */
  autoTrackExceptions?: boolean;
  /**
   * Web auto-capture switches. Every switch defaults to on; set one to
   * `false` to turn that capture off.
   */
  autocapture?: {
    /**
     * Emit `page_viewed` on init and on every URL change (`popstate`,
     * `history.pushState`, `history.replaceState`), plus the paired
     * `page_left` carrying `time_on_page_ms` on `pagehide` and
     * `visibilitychange:hidden`. Each `page_viewed` carries a `pageview_id`
     * and a per-tab `window_id` so the pair can be joined server-side.
     * @default true
     */
    pageViews?: boolean;
    /**
     * Emit the paired `page_left` (with `time_on_page_ms`) when the tab is
     * hidden, unloaded, or the client shuts down. Only read while
     * `pageViews` is on.
     * @default true
     */
    pageLeaves?: boolean;
  };
  /**
   * Web session-replay configuration. Pass `{ enabled: true }` to opt-in.
   * Recording is gated by sample rate (from remote config or `sampleRate`)
   * and consent state. rrweb is a peer dependency — install it separately.
   *
   * @default undefined (recording off)
   */
  replay?: ReplayConfig;
  /**
   * Tier 8 — surveys & in-product messaging configuration. Default off
   * so surveys never render until the consumer explicitly opts in.
   */
  surveys?: {
    /** Enable the surveys module. @default false */
    enabled?: boolean;
    /** Per-page client-side sample rate for `surveys.showFirstEligible()`. */
    sampleRate?: number;
    /** Caller-supplied person properties used in targeting evaluation. */
    personProperties?: Record<string, unknown>;
    /** Optional feature-flag resolver — defers to consumer's flag engine. */
    resolveFeatureFlag?: (flagKey: string) => boolean | string | null | undefined;
  };
  /**
   * Tier 4: optional bootstrap data for feature flags. SSR / pre-render
   * layers can seed flag values + payloads here so the first render after
   * hydration doesn't flicker from default-off to actual-value while the
   * first /config fetch is in flight.
   *
   * ```ts
   * new LayersClient({
   *   appId: 'app',
   *   bootstrap: {
   *     featureFlags: { 'new-checkout': true, 'pricing-experiment': 'variant-a' },
   *     featureFlagPayloads: { 'pricing-experiment': { discount: 0.1 } }
   *   }
   * });
   * ```
   */
  bootstrap?: FeatureFlagBootstrap;
}

/**
 * Attribution data that will be attached to all subsequent events.
 * Matches the RN SDK's setAttributionData pattern.
 */
export interface WebAttributionData {
  deeplinkId?: string | null;
  gclid?: string | null;
  fbclid?: string | null;
  ttclid?: string | null;
  msclkid?: string | null;
}

export type ErrorListener = (error: Error) => void;

/**
 * Listener for SDK initialization timing metrics.
 *
 * @param mainThreadDurationMs Time spent in the synchronous portion of init.
 * @param totalDurationMs Total wall-clock time of the `init()` call,
 *   including all async work (remote config fetch, etc.).
 */
export type InitListener = (mainThreadDurationMs: number, totalDurationMs: number) => void;

// --- localStorage keys ---
const INSTALL_ID_KEY = 'layers_install_id';
const FIRST_LAUNCH_TRACKED_KEY = 'layers_first_launch_tracked';
const FIRST_INSTALL_TIME_KEY = 'layers_first_install_time';
const ATTRIBUTION_DATA_KEY = 'layers_attribution_data';

/** Maximum age of an installation for install event gating (24 hours). */
const INSTALL_EVENT_MAX_DIFF_MS = 24 * 60 * 60 * 1000;

export class LayersClient {
  private core: LayersCore;
  private appUserId: string | undefined;
  private isOnline = true;
  private readonly enableDebug: boolean;
  private readonly baseUrl: string;
  private readonly config: LayersConfig;

  // Stored listener references for cleanup on shutdown
  private onlineListener: (() => void) | null = null;
  private offlineListener: (() => void) | null = null;
  private visibilityListener: (() => void) | null = null;
  private beforeUnloadListener: (() => void) | null = null;
  private deepLinkUnsubscribe: (() => void) | null = null;
  private exceptionUninstall: (() => void) | null = null;
  private pageviewHandle: PageviewTrackingHandle | null = null;

  // Error listeners
  private readonly errorListeners: Set<ErrorListener> = new Set();

  // Install ID
  private _installId: string | null = null;

  // Whether the SDK had prior state (install_id existed) at initialization time.
  private _hadPriorSdkState = false;

  // Attribution data stored for attachment to subsequent events.
  private _attributionData: WebAttributionData | null = null;

  // Recent events buffer for debug overlay (circular, last N events)
  private static readonly MAX_RECENT_EVENTS = 20;
  private _recentEvents: string[] = [];
  private _isInitialized = false;
  // Bumped by shutdown(). init() re-checks it after every await, so a
  // shutdown that lands mid-init stops the run before it installs listeners
  // and History patches that nothing would ever remove.
  private _initGeneration = 0;

  // Init timing listener
  private _initListener: InitListener | null = null;

  // Debug overlay state
  private _debugOverlayActive = false;

  // Quick flush timer for debounced delivery of initial events.
  // Disabled after the first regular flush interval fires to avoid
  // reducing the effective flush interval for the entire session.
  private quickFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private quickFlushEnabled = true;
  private quickFlushDisableTimer: ReturnType<typeof setTimeout> | null = null;

  // Public replay manager (constructed in init() when replay.enabled === true).
  // Exposed read-only via `replay` getter.
  private _replay: ReplayManager | null = null;
  /** Tier 8 — surveys & in-product messaging (rebuilt on the WASM core upgrade in init()). */
  private _surveys: SurveysModule;
  get surveys(): SurveysModule {
    return this._surveys;
  }

  constructor(config: LayersConfig) {
    this.enableDebug = config.enableDebug ?? false;
    this.baseUrl = (config.baseUrl ?? 'https://in.layers.com').replace(/\/$/, '');
    this.appUserId = config.appUserId;
    this.config = config;

    // Note: in browsers the WASM binary loads asynchronously, so this core
    // necessarily starts on the JS fallback engine. init() upgrades it to
    // the real Rust core once initWasm() resolves.
    this.core = this.buildCore();

    if (this.appUserId) {
      this.core.identify(this.appUserId);
    }

    this._surveys = this.buildSurveys();
  }

  /**
   * Construct the LayersCore from the stored config. Called by the
   * constructor (pre-WASM, fallback engine) and again by init() once the
   * WASM binary is ready (real Rust engine).
   */
  private buildCore(): LayersCore {
    const config = this.config;
    return LayersCore.init({
      config: {
        appId: config.appId,
        environment: config.environment ?? 'production',
        ...(config.baseUrl != null && { baseUrl: config.baseUrl }),
        ...(config.trackingBaseUrl != null && { trackingBaseUrl: config.trackingBaseUrl }),
        ...(config.enableDebug != null && { enableDebug: config.enableDebug }),
        ...(config.debug != null && { debug: config.debug }),
        flushIntervalMs: config.flushIntervalMs ?? 10_000,
        ...(config.flushThreshold != null && { flushThreshold: config.flushThreshold }),
        ...(config.maxQueueSize != null && { maxQueueSize: config.maxQueueSize }),
        sdkVersion: `client/${SDK_VERSION}`,
        ...(config.bootstrap != null && { bootstrap: config.bootstrap })
      },
      httpClient: new FetchHttpClient(),
      persistence: createDefaultPersistence(config.appId)
    });
  }

  /**
   * Construct the SurveysModule bound to the CURRENT core. Rebuilt by
   * init() after the WASM core upgrade — the module holds a core
   * reference, so it must not keep pointing at the shut-down fallback
   * core. (Tier 8 — wraps the WASM survey manager + DOM renderer; bridges
   * localStorage so frequency caps survive reloads.)
   */
  private buildSurveys(): SurveysModule {
    const config = this.config;
    const surveysPersistence = this.buildSurveysPersistence(config.appId);
    const surveysOptions: import('./surveys/module.js').SurveysModuleOptions = {
      enabled: config.surveys?.enabled ?? false
    };
    if (config.surveys?.sampleRate !== undefined) {
      surveysOptions.sampleRate = config.surveys.sampleRate;
    }
    if (config.surveys?.personProperties) {
      surveysOptions.personProperties = config.surveys.personProperties;
    }
    if (config.surveys?.resolveFeatureFlag) {
      surveysOptions.resolveFeatureFlag = config.surveys.resolveFeatureFlag;
    }
    if (surveysPersistence) {
      surveysOptions.persistence = surveysPersistence;
    }
    return new SurveysModule(this.core, surveysOptions);
  }

  /** Bridge `localStorage` to the SurveysModule's tiny persistence interface. */
  private buildSurveysPersistence(
    appId: string
  ): { read(key: string): string | null; write(key: string, value: string): void } | undefined {
    if (typeof localStorage === 'undefined') return undefined;
    const ns = `layers:${appId}:`;
    return {
      read: (key) => {
        try {
          return localStorage.getItem(ns + key);
        } catch {
          return null;
        }
      },
      write: (key, value) => {
        try {
          localStorage.setItem(ns + key, value);
        } catch {
          // Quota / SecurityError — best effort.
        }
      }
    };
  }

  /** Initialize the client: detects device info, attaches lifecycle listeners, and fetches remote config. */
  async init(): Promise<void> {
    const initStartTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const generation = this._initGeneration;

    // Upgrade to the real Rust/WASM engine. Browsers load WASM
    // asynchronously, so the constructor's core starts on the JS fallback;
    // swap it here BEFORE the rest of init so everything downstream
    // (remote config, app_open, replay) runs on the real engine. Anything
    // tracked between construction and init() survives the swap:
    // shutdown() durably persists both queues (snapshot semantics) and the
    // rebuilt core re-hydrates them.
    // initWasm() swallows load failures internally — isWasmReady() is the
    // truthful signal, so gate the swap on it (a failed load must not
    // churn through a pointless fallback→fallback rebuild).
    const wasmUrl = this.config.wasmUrl;
    await initWasm(wasmUrl === undefined ? undefined : { wasmUrl });
    if (generation !== this._initGeneration) return;
    if (isWasmReady() && !this.core.isWasmActive()) {
      // Carry in-memory state (consent, super properties, groups, device
      // context) across the swap — only queued events migrate via the
      // shutdown-persist + hydration path.
      // `migration.identity.sessionNumber` is what stops this page load being
      // counted twice: both cores read the SAME localStorage record and both
      // claim a session at init, so without the hand-off in
      // `applyMigrationState` the load spans two session numbers and the
      // counter climbs by two per navigation.
      const migration = this.core.exportMigrationState();
      this.core.shutdown();
      this.core = this.buildCore();
      this.core.applyMigrationState(migration);
      if (this.appUserId) {
        this.core.identify(this.appUserId);
      }
      this._surveys = this.buildSurveys();
    }
    if (!this.core.isWasmActive()) {
      // One loud, non-debug signal: the fallback delivers events but
      // server-driven controls are limited and the Rust core is the
      // tested production engine. This was previously silent — the
      // fallback ran in production for every bundler consumer (audit C2).
      console.warn(
        '[Layers] Running on the JS fallback engine — the Rust/WASM core failed to load. ' +
          'Verify layers_core_bg.wasm is served (check bundler asset handling and CSP).'
      );
    }

    // Synchronous init work
    this.initializeInstallId();
    this.initializeDeviceInfo();
    this.setupNetworkListener();
    this.setupLifecycleListeners();
    this.installPageviewTracking();
    captureAttribution();
    this.restoreAttributionData();

    // Main thread init complete — record timing
    const mainThreadEndTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const mainThreadDurationMs = Math.round(mainThreadEndTime - initStartTime);

    // Async init work
    await this.core.fetchRemoteConfig().catch(() => {
      // Remote config fetch is best-effort
    });
    if (generation !== this._initGeneration) return;

    // Replay module (after remote config so we know the sample rate)
    this.installReplayIfEnabled();

    // Install event gating + app_open
    if (this.config.autoTrackAppOpen !== false) {
      const appOpenProps: Record<string, unknown> = {};
      const isFirstLaunch = this.shouldTreatAsNewInstall();
      appOpenProps.is_first_launch = isFirstLaunch;

      if (isFirstLaunch) {
        this.storageSet(FIRST_LAUNCH_TRACKED_KEY, 'true');
      }

      // Send app_install before app_open on first launch for CAPI forwarding
      if (isFirstLaunch) {
        this.track('app_install', appOpenProps);
      }
      this.track('app_open', appOpenProps);
    }

    // Auto-track deep link events
    if (this.config.autoTrackDeepLinks !== false) {
      this.setupDeepLinkAutoTracking();
    }

    // Auto-capture uncaught exceptions + unhandled promise rejections
    if (this.config.autoTrackExceptions !== false) {
      this.exceptionUninstall = installExceptionAutoCapture(
        (eventName, properties) => {
          this.track(eventName, properties);
        },
        {
          onInternalError: (message, error) => {
            if (this.enableDebug) console.warn('[Layers]', message, error);
          }
        }
      );
    }

    this._isInitialized = true;

    // Measure total duration
    const totalEndTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const totalDurationMs = Math.round(totalEndTime - initStartTime);

    // Track init timing event
    this.trackInitTiming(mainThreadDurationMs, totalDurationMs);

    if (this.enableDebug) {
      console.log(
        `[Layers] Init timing: mainThread=${String(mainThreadDurationMs)}ms, total=${String(totalDurationMs)}ms`
      );
    }

    // Notify init listener
    if (this._initListener) {
      try {
        this._initListener(mainThreadDurationMs, totalDurationMs);
      } catch (e) {
        if (this.enableDebug) {
          console.warn('[Layers] InitListener threw:', e);
        }
      }
    }

    // Flush immediately after init to ensure app_open is delivered
    // before user potentially closes the tab
    this.core.flushAsync().catch(() => {});

    // Disable quick flush after the first regular flush interval fires.
    // Quick flush is only needed during the initial window (before the
    // periodic flush timer has a chance to fire).
    const flushInterval = this.config.flushIntervalMs ?? 10_000;
    this.quickFlushDisableTimer = setTimeout(() => {
      this.quickFlushEnabled = false;
      this.quickFlushDisableTimer = null;
    }, flushInterval);
  }

  /**
   * Record a custom analytics event with an optional property bag.
   *
   * Events are batched and flushed automatically when the queue reaches
   * `flushThreshold` or the periodic flush timer fires.
   */
  track(eventName: string, properties?: EventProperties): void {
    if (this.enableDebug) {
      console.log(
        `[Layers] track("${eventName}", ${Object.keys(properties ?? {}).length} properties)`
      );
    }
    try {
      const merged = this.mergeAllProperties(properties);
      const depthBefore = this.core.queueDepth();
      this.core.track(eventName, merged, this.appUserId);
      const depthAfter = this.core.queueDepth();

      // Queue depth gating: verify event was accepted by core
      if (depthAfter <= depthBefore && this.enableDebug) {
        console.warn(
          `[Layers] track("${eventName}") — event may not have been queued ` +
            `(depth before=${String(depthBefore)}, after=${String(depthAfter)})`
        );
      }

      // Record for debug overlay
      this.recordRecentEvent(eventName, properties);

      // Schedule a debounced flush for quick delivery of initial events
      this.scheduleQuickFlush();
    } catch (e) {
      this.emitError(e);
    }
  }

  /**
   * Record a screen view event with an optional property bag.
   *
   * Events are batched and flushed automatically when the queue reaches
   * `flushThreshold` or the periodic flush timer fires.
   */
  screen(screenName: string, properties?: EventProperties): void {
    if (this.enableDebug) {
      console.log(
        `[Layers] screen("${screenName}", ${Object.keys(properties ?? {}).length} properties)`
      );
    }
    try {
      const merged = this.mergeAllProperties(properties);
      const depthBefore = this.core.queueDepth();
      this.core.screen(screenName, merged, this.appUserId);
      const depthAfter = this.core.queueDepth();

      if (depthAfter <= depthBefore && this.enableDebug) {
        console.warn(
          `[Layers] screen("${screenName}") — event may not have been queued ` +
            `(depth before=${String(depthBefore)}, after=${String(depthAfter)})`
        );
      }

      this.recordRecentEvent(`screen:${screenName}`, properties);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Set or update user-level properties that persist across sessions. */
  setUserProperties(properties: UserProperties): void {
    this.core.setUserProperties(properties);
    this.sendUserPropertiesAsync(properties as Record<string, unknown>, false);
  }

  /** Set user-level properties with "set once" semantics — only keys not previously set are applied. */
  setUserPropertiesOnce(properties: UserProperties): void {
    this.core.setUserPropertiesOnce(properties);
    this.sendUserPropertiesAsync(properties as Record<string, unknown>, true);
  }

  /** Update the user's consent state for analytics and advertising data collection. */
  setConsent(consent: ConsentState): void {
    this.core.setConsent(consent);
    // Replay recorder needs to be told — it might need to start (Denied -> Granted)
    // or stop and discard buffered chunks (Granted -> Denied).
    if (this._replay) {
      try {
        this._replay.onConsentChange();
      } catch {
        // best effort
      }
    }
  }

  // ── Tier 1: super-properties ─────────────────────────────────────────

  /**
   * Register one or more super-properties — auto-merged into every track /
   * screen call until cleared. Use for app-wide context (plan tier, region,
   * experiment bucket).
   */
  setSuperProperties(properties: Record<string, unknown>): void {
    try {
      this.core.setSuperProperties(properties);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Register super-properties only if their keys have not been set before. */
  setSuperPropertiesOnce(properties: Record<string, unknown>): void {
    try {
      this.core.setSuperPropertiesOnce(properties);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Remove a single super-property by key. */
  unregisterSuperProperty(key: string): void {
    try {
      this.core.unregisterSuperProperty(key);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Clear all registered super-properties. */
  clearSuperProperties(): void {
    try {
      this.core.clearSuperProperties();
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Snapshot the currently-registered super-properties. */
  getSuperProperties(): Record<string, unknown> {
    return this.core.getSuperProperties();
  }

  // ── Tier 1: timed events ─────────────────────────────────────────────

  /** Start a duration timer for the next `track(name)` call. */
  timeEvent(eventName: string): void {
    try {
      this.core.timeEvent(eventName);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Cancel a timed event without emitting it. Returns elapsed ms (0 if none). */
  cancelTimedEvent(eventName: string): number {
    try {
      return this.core.cancelTimedEvent(eventName);
    } catch (e) {
      this.emitError(e);
      return 0;
    }
  }

  // ── Tier 1: multi-group ──────────────────────────────────────────────

  /**
   * Set membership for a single group_type. Empty `groupId` removes the
   * type. Use this in addition to `group()` to track multiple group axes
   * (company + team + project).
   */
  setGroup(groupType: string, groupId: string): void {
    try {
      this.core.setGroup(groupType, groupId);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Add a group membership without overwriting other types (alias for setGroup). */
  addGroup(groupType: string, groupId: string): void {
    try {
      this.core.addGroup(groupType, groupId);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Remove a group_type from the membership map. */
  removeGroup(groupType: string): void {
    try {
      this.core.removeGroup(groupType);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Snapshot the current $groups membership map. */
  getGroups(): GroupsState {
    return this.core.getGroups();
  }

  // ── Tier 1: user-property mutators ───────────────────────────────────

  /** Increment a numeric user property by `delta` (negative allowed). */
  increment(key: string, delta: number = 1): void {
    try {
      this.core.increment(key, delta);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Append a single value to a list-typed user property. */
  append(key: string, value: unknown): void {
    try {
      this.core.append(key, value);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Union (set-add) values into a list-typed user property. */
  union(key: string, values: unknown[]): void {
    try {
      this.core.union(key, values);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Remove a user property by key. */
  unset(key: string): void {
    try {
      this.core.unset(key);
    } catch (e) {
      this.emitError(e);
    }
  }

  // ── Tier 1: identity reset ───────────────────────────────────────────

  /**
   * Clear identity, super-properties, and groups — rotates the device /
   * anonymous IDs and starts a new session. Call this on user logout so
   * subsequent events do not carry the previous user's identity.
   */
  reset(): void {
    if (this.enableDebug) console.log('[Layers] reset()');
    this.appUserId = undefined;
    try {
      this.core.reset();
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Returns the current anonymous ID, rotated on `reset()`. */
  getAnonymousId(): string | null {
    return this.core.getAnonymousId();
  }

  /** Returns the current device ID (stable per install, rotated on `reset()`). */
  getDeviceId(): string | null {
    return this.core.getDeviceId();
  }

  /** Returns the monotonically-increasing session number. */
  getSessionNumber(): number {
    return this.core.getSessionNumber();
  }

  /** Returns the SDK first-open RFC3339 timestamp, or null. */
  getFirstOpenTime(): string | null {
    return this.core.getFirstOpenTime();
  }

  // ── Tier 1: before_send hook ─────────────────────────────────────────

  /**
   * Register a synchronous filter callback called for every track / screen
   * before the event reaches the queue. Return `null` to drop, or a modified
   * `BeforeSendEvent` to forward.
   *
   * Pass `null` to clear the hook.
   */
  setBeforeSend(hook: BeforeSendHook | null): void {
    this.core.setBeforeSend(hook);
  }

  // ── Tier 4: feature flags ──────────────────────────────────────────

  /**
   * Evaluate a feature flag. Returns `undefined` for unknown flags so the
   * `??` default-value pattern works:
   *
   * ```ts
   * const variant = client.getFeatureFlag('pricing-experiment') ?? 'control';
   * ```
   *
   * Side-effect: emits `$feature_flag_called` at most once per
   * `(flag_key, response)` pair per session — the dedup is handled by the
   * Rust core.
   */
  getFeatureFlag(flagKey: string): FeatureFlagValue | undefined {
    return this.core.getFeatureFlag(flagKey);
  }

  /** Convenience: returns `true` iff `getFeatureFlag(flagKey)` is truthy. */
  isFeatureEnabled(flagKey: string): boolean {
    return this.core.isFeatureEnabled(flagKey);
  }

  /**
   * Look up the JSON payload attached to a flag. Does NOT emit
   * `$feature_flag_called` — use `getFeatureFlag(...)` for the exposure
   * event.
   */
  getFeatureFlagPayload<T = unknown>(flagKey: string): T | undefined {
    return this.core.getFeatureFlagPayload<T>(flagKey);
  }

  /** Snapshot every accessible flag's current value. Does NOT emit exposure events. */
  getAllFlags(): Record<string, FeatureFlagValue> {
    return this.core.getAllFlags();
  }

  /**
   * Force a re-fetch of `/config` and refresh feature flag definitions.
   * Fires every `onFeatureFlags` listener after the refresh completes.
   */
  async reloadFeatureFlags(): Promise<void> {
    return this.core.reloadFeatureFlags();
  }

  /** Override person properties used for flag evaluation only. */
  setPersonPropertiesForFlags(properties: Record<string, unknown>): void {
    try {
      this.core.setPersonPropertiesForFlags(properties);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Merge additional person properties into the flag-evaluation map. */
  mergePersonPropertiesForFlags(properties: Record<string, unknown>): void {
    try {
      this.core.mergePersonPropertiesForFlags(properties);
    } catch (e) {
      this.emitError(e);
    }
  }

  /**
   * Subscribe to feature-flag refreshes. Fires after `reloadFeatureFlags()`
   * or `setFeatureFlagBootstrap()` completes.
   *
   * Returns a function that removes the subscription. Idempotent.
   */
  onFeatureFlags(callback: FeatureFlagsListener): () => void {
    return this.core.onFeatureFlags(callback);
  }

  /** Seed bootstrap flag values + payloads after init (delayed SSR / edge fetches). */
  setFeatureFlagBootstrap(bootstrap: FeatureFlagBootstrap): void {
    this.core.setFeatureFlagBootstrap(bootstrap);
  }

  /**
   * Associate all subsequent events with a group (company, team, organization).
   * Pass `undefined` or empty string to clear the group association.
   */
  group(groupId: string | undefined, properties?: EventProperties): void {
    if (this.enableDebug) {
      console.log(
        `[Layers] group(${groupId ? `"${groupId}"` : 'undefined'}, ${Object.keys(properties ?? {}).length} properties)`
      );
    }
    try {
      this.core.group(groupId ?? '', properties);
    } catch (e) {
      this.emitError(e);
    }
  }

  /** Associate all subsequent events with the given user ID, or clear it with `undefined`. */
  setAppUserId(appUserId: string | undefined): void {
    if (this.enableDebug) {
      console.log(`[Layers] setAppUserId(${appUserId ? `"${appUserId}"` : 'undefined'})`);
    }
    this.appUserId = appUserId;
    if (appUserId) {
      this.core.identify(appUserId);
    } else {
      this.core.identify('');
    }
  }

  /** @deprecated Use setAppUserId instead */
  setUserId(userId: string): void {
    this.setAppUserId(userId);
  }

  /** Return the current app user ID, or `undefined` if not set. */
  getAppUserId(): string | undefined {
    return this.appUserId;
  }

  /** @deprecated Use getAppUserId instead */
  getUserId(): string | undefined {
    return this.appUserId;
  }

  /** Return the current anonymous session ID. */
  getSessionId(): string {
    return this.core.getSessionId();
  }

  /** Return the current consent state for analytics and advertising. */
  getConsentState(): ConsentState {
    return this.core.getConsentState();
  }

  /** Override device-level context fields (platform, OS, locale, etc.). */
  setDeviceInfo(deviceInfo: DeviceContext): void {
    this.core.setDeviceContext(deviceInfo);
  }

  /**
   * Return the install ID (persistent UUID stored in localStorage).
   * Generated on first init, persists across sessions.
   */
  getInstallId(): string | null {
    return this._installId;
  }

  /**
   * Return the number of events currently queued.
   */
  getQueueDepth(): number {
    return this.core.queueDepth();
  }

  /**
   * Return the per-device DebugView token.
   *
   * When `debug: true` was passed to the constructor, the SDK generates a
   * stable UUID and persists it in localStorage. That token is sent in the
   * `X-Debug-Token` header on every event upload and can be displayed in
   * dev UIs to filter the live-tail view to this device only.
   *
   * Returns `null` when debug mode is off.
   */
  getDebugToken(): string | null {
    return this.core.getDebugToken();
  }

  /**
   * Store attribution data that will be attached to all subsequent events.
   *
   * The values are persisted in localStorage so they survive page reloads.
   * Pass `null` to clear a value.
   *
   * When set, `deeplinkId`, `gclid`, `fbclid`, `ttclid`, and/or `msclkid`
   * are included in every event's properties.
   */
  setAttributionData(data: WebAttributionData): void {
    this._attributionData = data;

    // Update the Rust core's DeviceContext with the deeplinkId so the
    // top-level event field is populated.
    try {
      const currentContext = this.core.getDeviceContext();
      const ctx: DeviceContext = { ...currentContext };
      if (data.deeplinkId) {
        ctx.deeplinkId = data.deeplinkId;
      } else {
        delete ctx.deeplinkId;
      }
      this.core.setDeviceContext(ctx);
    } catch {
      // DeviceContext update is best-effort
    }

    // Persist to localStorage
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(ATTRIBUTION_DATA_KEY, JSON.stringify(data));
      }
    } catch {
      // localStorage write is best-effort
    }

    if (this.enableDebug) {
      console.log(`[Layers] setAttributionData(${JSON.stringify(data)})`);
    }
  }

  /**
   * Set a listener to receive SDK initialization timing metrics.
   * Must be called **before** `init()` to receive the callback.
   * Pass `null` to clear the listener.
   */
  setInitListener(listener: InitListener | null): void {
    this._initListener = listener;
  }

  /**
   * Enable the debug overlay — a lightweight DOM panel showing SDK state.
   * The overlay updates every second with queue depth, session info, and recent events.
   */
  enableDebugOverlay(): void {
    this._debugOverlayActive = true;
    showDebugOverlay(() => ({
      sdkVersion: SDK_VERSION,
      queueDepth: this.core.queueDepth(),
      sessionId: this.core.getSessionId(),
      installId: this._installId,
      appId: this.config.appId,
      environment: this.config.environment ?? 'production',
      isOnline: this.isOnline,
      recentEvents: this._recentEvents
    }));
  }

  /**
   * Disable and remove the debug overlay from the DOM.
   */
  disableDebugOverlay(): void {
    this._debugOverlayActive = false;
    hideDebugOverlay();
  }

  /** Whether the SDK has completed async initialization. */
  get isInitialized(): boolean {
    return this._isInitialized;
  }

  /** SDK version string. */
  getSdkVersion(): string {
    return SDK_VERSION;
  }

  /**
   * Returns the most recent tracked events (newest first).
   * Each entry is a formatted string like "12:34:56 event_name (3 props)".
   */
  getRecentEvents(): readonly string[] {
    return this._recentEvents;
  }

  /** Flush all queued events to the server. Falls back to synchronous persistence on failure. */
  async flush(): Promise<void> {
    try {
      await this.core.flushAsync();
    } catch (e) {
      this.emitError(e);
      this.core.flush();
    }
  }

  /** Immediately shut down the client, removing all event listeners. Queued events are persisted but not flushed. */
  shutdown(): void {
    this._initGeneration += 1;
    this.cleanupListeners();
    if (this._debugOverlayActive) {
      this.disableDebugOverlay();
    }
    if (this._replay) {
      try {
        this._replay.destroy();
      } catch {
        // best effort
      }
      this._replay = null;
    }
    this._attributionData = null;
    this._initListener = null;
    this.core.shutdown();
  }

  /**
   * Async shutdown: flushes remaining events before shutting down.
   * @param timeoutMs Maximum time to wait for flush (default 3000ms).
   */
  async shutdownAsync(timeoutMs = 3000): Promise<void> {
    this._initGeneration += 1;
    this.cleanupListeners();
    if (this._debugOverlayActive) {
      this.disableDebugOverlay();
    }
    if (this._replay) {
      try {
        this._replay.destroy();
      } catch {
        // best effort
      }
      this._replay = null;
    }
    this._attributionData = null;
    this._initListener = null;
    try {
      await Promise.race([
        this.core.flushAsync(),
        new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))
      ]);
    } catch {
      // Best effort — proceed with shutdown
    }
    this.core.shutdown();
  }

  private cleanupListeners(): void {
    if (this.quickFlushTimer) {
      clearTimeout(this.quickFlushTimer);
      this.quickFlushTimer = null;
    }
    if (this.quickFlushDisableTimer) {
      clearTimeout(this.quickFlushDisableTimer);
      this.quickFlushDisableTimer = null;
    }
    if (typeof window !== 'undefined') {
      if (this.onlineListener) {
        window.removeEventListener('online', this.onlineListener);
        this.onlineListener = null;
      }
      if (this.offlineListener) {
        window.removeEventListener('offline', this.offlineListener);
        this.offlineListener = null;
      }
      if (this.beforeUnloadListener) {
        window.removeEventListener('beforeunload', this.beforeUnloadListener);
        this.beforeUnloadListener = null;
      }
    }
    if (typeof document !== 'undefined' && this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
    if (this.deepLinkUnsubscribe) {
      this.deepLinkUnsubscribe();
      this.deepLinkUnsubscribe = null;
    }
    if (this.exceptionUninstall) {
      this.exceptionUninstall();
      this.exceptionUninstall = null;
    }
    if (this.pageviewHandle) {
      this.pageviewHandle.shutdown();
      this.pageviewHandle = null;
    }
  }

  /** End the current session and start a new one with a fresh session ID. */
  startNewSession(): void {
    this.core.startNewSession();
  }

  /**
   * Register an error listener. Errors from track/screen/flush
   * that would otherwise be silently dropped are forwarded here.
   */
  on(event: 'error', listener: ErrorListener): this {
    if (event === 'error') this.errorListeners.add(listener);
    return this;
  }

  /**
   * Remove a previously registered error listener.
   */
  off(event: 'error', listener: ErrorListener): this {
    if (event === 'error') this.errorListeners.delete(listener);
    return this;
  }

  private emitError(error: unknown): void {
    const err = error instanceof Error ? error : new Error(String(error));
    for (const listener of this.errorListeners) {
      try {
        listener(err);
      } catch {}
    }
    if (this.enableDebug && this.errorListeners.size === 0) {
      console.warn('[Layers]', err.message);
    }
  }

  // --- Install ID persistence ---

  private initializeInstallId(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const existing = localStorage.getItem(INSTALL_ID_KEY);
      if (existing) {
        this._installId = existing;
        this._hadPriorSdkState = true;
      } else {
        const newId = generateUUID();
        localStorage.setItem(INSTALL_ID_KEY, newId);
        this._installId = newId;
        this._hadPriorSdkState = false;

        // Store first install timestamp for install event gating
        if (!localStorage.getItem(FIRST_INSTALL_TIME_KEY)) {
          localStorage.setItem(FIRST_INSTALL_TIME_KEY, String(Date.now()));
        }
      }
    } catch {
      // localStorage unavailable — generate an in-memory ID
      this._installId = generateUUID();
    }
  }

  // --- Install event gating (24h window) ---

  private shouldTreatAsNewInstall(): boolean {
    // If first_launch was already tracked, this is not a first launch
    const flag = this.storageGet(FIRST_LAUNCH_TRACKED_KEY);
    if (flag === 'true') return false;

    // If SDK had prior state (install_id existed), trust the flag
    if (this._hadPriorSdkState) return true;

    // SDK has no prior state — check whether the install is recent
    const installTimeStr = this.storageGet(FIRST_INSTALL_TIME_KEY);
    if (installTimeStr) {
      const installTime = Number(installTimeStr);
      if (Number.isFinite(installTime)) {
        const elapsed = Date.now() - installTime;
        const isRecentInstall = elapsed <= INSTALL_EVENT_MAX_DIFF_MS;

        if (!isRecentInstall && this.enableDebug) {
          console.log(
            `[Layers] Install event gated: installed ${Math.round(elapsed / 1000)}s ago ` +
              `(threshold=${INSTALL_EVENT_MAX_DIFF_MS / 1000}s), ` +
              'no prior SDK state — suppressing is_first_launch'
          );
        }

        return isRecentInstall;
      }
    }

    // If we can't read the install time, default to trusting it as a new install
    return true;
  }

  // --- Attribution data ---

  private restoreAttributionData(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const raw = localStorage.getItem(ATTRIBUTION_DATA_KEY);
      if (!raw) return;
      const data: WebAttributionData = JSON.parse(raw);
      this._attributionData = data;

      // Sync restored deeplinkId to the Rust core's DeviceContext
      if (data.deeplinkId) {
        try {
          const currentContext = this.core.getDeviceContext();
          this.core.setDeviceContext({ ...currentContext, deeplinkId: data.deeplinkId });
        } catch {
          // best-effort
        }
      }

      if (this.enableDebug && data) {
        console.log(`[Layers] Restored attribution data: ${JSON.stringify(data)}`);
      }
    } catch {
      // Restoration is best-effort
    }
  }

  /**
   * Merge attribution properties into event properties.
   * deeplink_id flows through DeviceContext; other fields flow through properties.
   */
  private mergeAttributionProperties(properties?: EventProperties): EventProperties | undefined {
    const data = this._attributionData;
    if (!data) return properties;

    const merged: Record<string, unknown> = { ...(properties ?? {}) };
    if (data.gclid) merged.gclid = data.gclid;
    if (data.fbclid) merged.fbclid = data.fbclid;
    if (data.ttclid) merged.ttclid = data.ttclid;
    if (data.msclkid) merged.msclkid = data.msclkid;
    return merged as EventProperties;
  }

  /** Merge CAPI, attribution (url-based), setAttributionData, and user properties. */
  private mergeAllProperties(properties?: EventProperties): EventProperties {
    const capiProps = getCapiProperties();
    const urlAttrProps = getAttributionProperties();
    const attrProps = this.mergeAttributionProperties(undefined) ?? {};
    return { ...capiProps, ...urlAttrProps, ...attrProps, ...properties } as EventProperties;
  }

  // --- Deep link auto-tracking ---

  private _trackedDeepLinkUrls = new Set<string>();

  private setupDeepLinkAutoTracking(): void {
    this.deepLinkUnsubscribe = setupDeepLinkListener((data: DeepLinkData) => {
      // Deduplicate
      if (this._trackedDeepLinkUrls.has(data.url)) return;
      this._trackedDeepLinkUrls.add(data.url);
      // Clear after a short delay so the same URL can be tracked again later
      setTimeout(() => this._trackedDeepLinkUrls.delete(data.url), 2000);

      try {
        const properties: Record<string, unknown> = {
          ...data.queryParams,
          url: data.url,
          scheme: data.scheme,
          host: data.host,
          path: data.path
        };
        this.track('deep_link_opened', properties);
        if (this.enableDebug) {
          console.log(`[Layers] auto-tracked deep_link_opened: ${data.url}`);
        }
      } catch (e) {
        this.emitError(e);
      }
    });
  }

  // --- Replay module ---

  /**
   * The replay manager, available after `init()` when `replay.enabled` is true.
   * Returns `null` when replay is disabled or before init has run.
   */
  get replay(): ReplayManager | null {
    return this._replay;
  }

  private installReplayIfEnabled(): void {
    if (this._replay) return; // already installed
    const userConfig = this.config.replay;
    if (!userConfig?.enabled) return;

    // Pull remote replay config, if present.
    let remoteReplay: { sample_rate?: number; sample_rate_override?: number } | undefined;
    try {
      const json = this.core.getRemoteConfigJson?.();
      if (json) {
        const parsed: unknown = JSON.parse(json);
        if (parsed && typeof parsed === 'object' && 'replay' in parsed) {
          const r = (parsed as { replay?: unknown }).replay;
          if (r && typeof r === 'object') {
            remoteReplay = r as typeof remoteReplay;
          }
        }
      }
    } catch {
      // Ignore — fall through to user defaults.
    }

    const resolved = resolveReplayConfig(userConfig, remoteReplay, this.baseUrl);

    this._replay = installReplay({
      config: resolved,
      appId: this.config.appId,
      sdkVersion: this.core.sdkVersionHeader(),
      enableDebug: this.enableDebug,
      getSessionId: () => this.core.getSessionId(),
      getPageviewId: () => this.pageviewHandle?.currentPageviewId(),
      isConsentGranted: () => this.isReplayConsentGranted()
    });
  }

  /**
   * Replay-specific consent gate. Returns false (don't record) when
   * analytics consent has been explicitly denied. Treats `undefined` as
   * "not yet decided" — recorder will start, and the host app can revoke
   * later via `setConsent()`.
   */
  private isReplayConsentGranted(): boolean {
    try {
      const state = this.core.getConsentState();
      // Legacy 2-flag shape: analytics === false means denied.
      if (state.analytics === false) return false;
      return true;
    } catch {
      return true;
    }
  }

  // --- Init timing ---

  private trackInitTiming(mainThreadDurationMs: number, totalDurationMs: number): void {
    try {
      this.core.track(
        'layers_init_timing',
        {
          duration_ms: totalDurationMs,
          main_thread_duration_ms: mainThreadDurationMs
        },
        this.appUserId
      );
    } catch {
      // Init timing is best-effort
    }
  }

  // --- Debounced quick flush (for initial events) ---

  private scheduleQuickFlush(): void {
    if (!this.quickFlushEnabled) return; // only active during initial window
    if (this.quickFlushTimer) return; // already scheduled
    this.quickFlushTimer = setTimeout(() => {
      this.quickFlushTimer = null;
      if (this.core.queueDepth() > 0) {
        this.core.flushAsync().catch(() => {});
      }
    }, 2000); // 2 second debounce
  }

  // --- Recent events buffer (for debug overlay) ---

  private recordRecentEvent(eventName: string, properties?: EventProperties): void {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    const propCount = Object.keys(properties ?? {}).length;
    const entry = `${time} ${eventName}${propCount > 0 ? ` (${String(propCount)} props)` : ''}`;
    this._recentEvents.unshift(entry);
    if (this._recentEvents.length > LayersClient.MAX_RECENT_EVENTS) {
      this._recentEvents.pop();
    }
  }

  // --- Platform-specific: browser device info ---

  private initializeDeviceInfo(): void {
    const context: DeviceContext = {
      platform: 'web',
      osVersion: this.detectOS(),
      appVersion: SDK_VERSION,
      deviceModel: this.detectDeviceModel(),
      locale: this.detectLocale(),
      screenSize: this.detectScreenSize(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(this._installId != null && { installId: this._installId })
    };
    this.core.setDeviceContext(context);
  }

  private detectOS(): string {
    if (typeof navigator === 'undefined') return 'unknown';
    const ua = navigator.userAgent;
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac OS')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'unknown';
  }

  private detectDeviceModel(): string {
    if (typeof navigator === 'undefined') return 'unknown';
    if ('userAgentData' in navigator) {
      const uaData = (navigator as { userAgentData?: { platform?: string } }).userAgentData;
      if (uaData?.platform) return uaData.platform;
    }
    return navigator.platform ?? 'unknown';
  }

  private detectLocale(): string {
    if (typeof navigator === 'undefined') return 'en-US';
    return navigator.language ?? 'en-US';
  }

  private detectScreenSize(): string {
    if (typeof window === 'undefined' || typeof screen === 'undefined') return 'unknown';
    return `${screen.width}x${screen.height}`;
  }

  // --- User properties HTTP POST ---

  /**
   * Fire-and-forget POST to /users/properties.
   * Best-effort: errors are silently swallowed.
   */
  private sendUserPropertiesAsync(properties: Record<string, unknown>, setOnce: boolean): void {
    const appUserId = this.appUserId ?? this.core.getSessionId();
    // The core's device_id — the same value already attached to every event.
    // The server joins the (device_id, user_id) pair to stitch anonymous
    // activity onto the identified profile, so omitting it here left every
    // /users/properties upsert unstitchable.
    const deviceId = this.core.getDeviceId();
    const payload: Record<string, unknown> = {
      app_id: this.config.appId,
      app_user_id: appUserId,
      properties,
      timestamp: new Date().toISOString()
    };
    if (deviceId) {
      payload.device_id = deviceId;
    }
    if (setOnce) {
      payload.set_once = true;
    }

    fetch(`${this.baseUrl}/users/properties`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-App-Id': this.config.appId,
        // From the core so this endpoint reports the same engine token as
        // /events; hand-building `client/${SDK_VERSION}` here made one install
        // report two different X-SDK-Version shapes.
        'X-SDK-Version': this.core.sdkVersionHeader()
      },
      body: JSON.stringify(payload)
    }).catch(() => {
      // Best-effort — don't throw on network errors
    });
  }

  // --- Platform-specific: network listener ---

  private setupNetworkListener(): void {
    if (typeof window === 'undefined') return;

    this.isOnline = navigator?.onLine ?? true;

    this.onlineListener = () => {
      this.isOnline = true;
      void this.core.flushAsync().catch(() => {});
    };
    this.offlineListener = () => {
      this.isOnline = false;
    };

    window.addEventListener('online', this.onlineListener);
    window.addEventListener('offline', this.offlineListener);
  }

  // --- Platform-specific: lifecycle listeners ---

  private getBeaconUrl(): string {
    return `${this.baseUrl}/events`;
  }

  /**
   * Auto-capture page views: `page_viewed` now and on every URL change, with
   * the paired `page_left`. Runs in the synchronous part of init(), ahead of
   * the remote-config fetch, so a visitor who leaves during that fetch is
   * still counted; anything tracked here queues in the core like any other
   * pre-init event. The module's initial-touch super-properties and
   * `$deep_link_opened` heuristic stay off: `attribution.ts` already records
   * the landing attribution, and `autoTrackDeepLinks` already emits
   * `deep_link_opened` for the same URLs.
   */
  private installPageviewTracking(): void {
    if (this.config.autocapture?.pageViews === false) return;
    // A second init() must never stack a second History patch under the
    // first; the module makes a second live install a no-op, and this keeps
    // the handle we hold current.
    this.pageviewHandle?.shutdown();
    try {
      this.pageviewHandle = installPageviewTracking(
        {
          track: (eventName, properties) => {
            this.track(eventName, properties);
          },
          setSuperPropertiesOnce: (properties) => {
            this.setSuperPropertiesOnce(properties);
          },
          enableDebug: this.enableDebug
        },
        {
          captureInitialTouch: false,
          trackDeepLinkOpened: false,
          trackPageleave: this.config.autocapture?.pageLeaves !== false
        }
      );
    } catch (e) {
      this.emitError(e);
    }
  }

  /**
   * Record a page view for a navigation the History API never saw (a memory
   * router, a framework that swaps views without touching the URL). A no-op
   * when `autocapture.pageViews` is off.
   */
  capturePageview(): void {
    this.pageviewHandle?.capturePageview();
  }

  private setupLifecycleListeners(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Track app_background / app_foreground on visibilitychange,
    // and flush on page hide using sendBeacon.
    this.visibilityListener = () => {
      if (document.visibilityState === 'hidden') {
        // Close the open page view first so `page_left` is in the queue the
        // beacon below carries; the module's own listener registers later
        // and would otherwise fire after the flush.
        this.pageviewHandle?.capturePageleave();

        // Track app_background lifecycle event
        try {
          this.core.track('app_background', {}, this.appUserId);
          this.recordRecentEvent('app_background');
        } catch {
          // best-effort
        }

        // Replay: flush any buffered chunks before the tab loses CPU.
        if (this._replay) {
          try {
            this._replay.flushOnPageHide();
          } catch {
            // best-effort
          }
        }

        if (
          typeof navigator !== 'undefined' &&
          navigator.sendBeacon &&
          this.core.queueDepth() > 0
        ) {
          try {
            const batch = this.core.createBeaconPayload();
            if (batch) {
              const sent = navigator.sendBeacon(this.getBeaconUrl(), batch);
              if (sent) {
                this.core.clearBeaconEvents();
                return;
              }
              // sendBeacon failed — requeue events before falling back to persistence
              this.core.requeueBeaconEvents();
              this.core.flush();
              return;
            }
          } catch {
            // sendBeacon threw — requeue any drained events before sync flush
            this.core.requeueBeaconEvents();
          }
        }
        this.core.flush();
      } else if (document.visibilityState === 'visible') {
        // Track app_foreground lifecycle event
        try {
          this.core.track('app_foreground', {}, this.appUserId);
          this.recordRecentEvent('app_foreground');
        } catch {
          // best-effort
        }

        // Flush queued events when returning to foreground
        if (this.isOnline) {
          void this.core.flushAsync().catch(() => {});
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);

    // Flush on beforeunload — try sendBeacon for reliable delivery, fall back to sync persistence
    this.beforeUnloadListener = () => {
      // Same ordering as the hidden branch: `page_left` goes in before the
      // queue is drained into the beacon.
      this.pageviewHandle?.capturePageleave();
      // Check sendBeacon availability BEFORE draining events to avoid losing them
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        try {
          const payload = this.core.createBeaconPayload();
          if (payload) {
            const url = this.getBeaconUrl();
            if (navigator.sendBeacon(url, payload)) {
              this.core.clearBeaconEvents();
              return;
            }
            // sendBeacon failed — requeue before falling back to persistence
            this.core.requeueBeaconEvents();
          }
        } catch {
          // sendBeacon threw — requeue any drained events
          this.core.requeueBeaconEvents();
        }
      }
      // Fall back to sync persistence (queue is intact if sendBeacon was unavailable)
      this.core.flush();
    };
    window.addEventListener('beforeunload', this.beforeUnloadListener);
  }

  // --- localStorage helpers ---

  private storageGet(key: string): string | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private storageSet(key: string, value: string): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(key, value);
    } catch {
      // localStorage full or unavailable
    }
  }
}

// --- UUID generation ---

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r % 4) + 8;
    return v.toString(16);
  });
}

// SDK version injected at build time by tsdown define
declare const __LAYERS_CLIENT_VERSION__: string;
const SDK_VERSION: string =
  typeof __LAYERS_CLIENT_VERSION__ !== 'undefined' ? __LAYERS_CLIENT_VERSION__ : '0.1.1-alpha.1';
